import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

/**
 * /hotel-lobby-ai-video-generator 的预设：两张照片 → 橙色棚双人说唱（2026-10-01）。
 *
 * 都走 reference 模式：多参考图，不需要参考视频。2026-10-16 起出片后服务端把音轨换成原曲
 * （packages/api 的 preset-audio.ts，按档位和秒数各一段）。这里只放 type import，
 * apps/web/test 的 transpile loader 才能直接加载。
 */
export const HOTEL_LOBBY_PAGE_PATH = "/hotel-lobby-ai-video-generator";

/** 登录前现场的 IndexedDB key，和 /image-to-video、首页分开 */
export const HOTEL_LOBBY_PENDING_STATE_KEY = "pendingHotelLobbyState";

export type HotelLobbyQualityId = "lite" | "standard" | "hd";

/**
 * 三档画质对位 aihotellobby.org 的 Lite / Standard / HD（2026-10-01）：
 * Lite 走 Seedance 2.0 Mini 480p，固定 4 秒 15 积分，正好是注册送的额度（2026-10-13 由 Grok 5 秒换过来，
 * 老公嫌 Grok 画质不够；同 prompt 实测 3/3）；Standard / HD 走 Seedance 2。
 * 720p 按档位闸门要 Starter，免费用户点 HD 走付费墙（hero 里处理）。
 */
export const HOTEL_LOBBY_QUALITIES: {
	id: HotelLobbyQualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration?: number;
}[] = [
	{ id: "lite", model: "seedance-2-mini", resolution: "480p", duration: 4 },
	{ id: "standard", model: "master-3.0", resolution: "480p" },
	{ id: "hd", model: "master-3.0", resolution: "720p" },
];

export const HOTEL_LOBBY_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as HotelLobbyQualityId,
	aspectRatioOptions: ["9:16", "16:9"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	/** Standard / HD 的 5 / 10 / 15；Lite 档自带固定 4 秒，不走这里 */
	durationOptions: [5, 10, 15],
	defaultDuration: 5,
	/** Left / Right 两个固定槽位，顺序即 reference_image_urls 顺序 */
	slotCount: 2,
} as const;

export type HotelLobbyTemplateId = "duet";

/**
 * 用户不直接编辑模板，只能在 composer 里加一句 director's note，
 * 拼到模板后面。note 的上限 = 1000 − 模板长度 − 前缀长度，hero 按这个算。
 */
export const HOTEL_LOBBY_NOTE_PREFIX = "\n\nDirector's note: ";

/**
 * 只看 note 是否全空白，不 trim 内容本身：hero 每敲一个字都会走这里，
 * trim 掉的话词与词之间的空格打不出来
 */
export function buildHotelLobbyPrompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${HOTEL_LOBBY_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/**
 * 历代模板（10/1 上线时人 / 宠物 / 家人三条，同日合成一条，10/16 改吊麦）都是这个开头。
 * 10/8 前提交的片没记页面，History 靠它认出是 Hotel Lobby 出的
 */
export const HOTEL_LOBBY_PROMPT_PATTERN = /^Two separate .{0,60}rap duet/;

/**
 * 一条通用模板 prompt（2026-10-01 由 People / Pets 合并）：aihotellobby.org 也只有一条，
 * 不让用户先选人还是宠物，一人一宠也不用纠结。比它多写了「人、宠物、角色都行」和
 * 「动物后腿站立像说唱歌手一样表演」。
 *
 * ≤ 1000 字（I2V_PROMPT_MAX_LENGTH）并给 note 留 ≥ 150 字，test/hotel-lobby-preset.test.mjs 卡住。
 * 措辞是自己写的，不抄竞品。prompt 里的 original beat / vocals 只用来让两人张嘴说唱，
 * 出片后音轨会被换成原曲。话筒是吊麦（2026-10-16 由落地麦架改回）：COLORS 原片、getstarrd、
 * aihotellobby.net、LightX 都是吊麦，只有 aihotellobby.org 用落地麦架。
 * 画幅不写进 prompt，由比例参数决定。保留数组形状，hero 只有一条时不显示模板 chip。
 */
export const HOTEL_LOBBY_TEMPLATES: {
	id: HotelLobbyTemplateId;
	prompt: string;
}[] = [
	{
		id: "duet",
		prompt: "Two separate photos become a rap duet in front of a seamless, bright orange studio wall. Subject from photo 1 on the LEFT, subject from photo 2 on the RIGHT. Each can be a person, pet or character; keep faces, fur, hair, skin tone and outfits exactly as in the photos. Animals stand upright on their hind legs and perform like rappers. One silver condenser mic hangs from above between them; no mic stand. The left one bounces, points and leans into the mic first; the right one stays cool, nods along, then takes the next verse. Original hip-hop beat and original vocals, mouths in sync. Medium-wide shot from the knees up, camera at chest height, slight handheld sway, soft even key light, background gently out of focus. No face blending, no swapping sides, no cuts, no extra people or animals, no text, no logos, no watermark.",
	},
];
